"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { formatNumber } from "@/lib/formatting/numbers";
import { isCheckoutQuantityAllowed } from "@shared/products/checkout";
import { useDepsChanged } from "@/hooks/use-synced-state";

interface QuantityStepperInput {
  quantity: number;
  minQuantity: number;
  onUpdateQuantity: (quantity: number) => void | Promise<void>;
  onPendingChange?: (pending: boolean) => void;
}

function useQuantityCommit({
  quantity,
  onUpdateQuantity,
  onPendingChange,
}: QuantityStepperInput) {
  const [localQty, setLocalQty] = useState(formatNumber(quantity));
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const onUpdateQuantityRef = useRef(onUpdateQuantity);
  const onPendingChangeRef = useRef(onPendingChange);
  const quantityRef = useRef(quantity);
  useEffect(() => {
    onUpdateQuantityRef.current = onUpdateQuantity;
    onPendingChangeRef.current = onPendingChange;
    quantityRef.current = quantity;
  });

  if (useDepsChanged([quantity])) {
    setLocalQty(formatNumber(quantity));
  }

  const cancelPending = useCallback(() => {
    clearTimeout(debounceRef.current);
    onPendingChangeRef.current?.(false);
  }, []);

  const commit = useCallback(
    async (value: number) => {
      cancelPending();
      setLocalQty(formatNumber(value));
      try {
        await onUpdateQuantityRef.current(value);
      } catch {
        setLocalQty(formatNumber(quantityRef.current));
      }
    },
    [cancelPending],
  );

  useEffect(() => cancelPending, [cancelPending]);

  return { localQty, setLocalQty, debounceRef, cancelPending, commit };
}

export function useQuantityStepper(input: QuantityStepperInput) {
  const { quantity, minQuantity, onPendingChange } = input;
  const { localQty, setLocalQty, debounceRef, cancelPending, commit } =
    useQuantityCommit(input);

  const typed = parseInt(localQty.replace(/[^0-9]/g, ""), 10);
  const base = isCheckoutQuantityAllowed(typed, minQuantity)
    ? typed
    : quantity;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/[^0-9]/g, "");
    setLocalQty(raw);
    const value = parseInt(raw, 10);
    if (isCheckoutQuantityAllowed(value, minQuantity)) {
      clearTimeout(debounceRef.current);
      onPendingChange?.(true);
      debounceRef.current = setTimeout(() => {
        void commit(value);
      }, 400);
    } else {
      clearTimeout(debounceRef.current);
      onPendingChange?.(true);
    }
  };

  const handleBlur = () => {
    const value = parseInt(localQty.replace(/[^0-9]/g, ""), 10);
    if (isCheckoutQuantityAllowed(value, minQuantity)) {
      commit(value);
    } else {
      cancelPending();
      setLocalQty(formatNumber(quantity));
    }
  };

  return { localQty, base, commit, handleChange, handleBlur };
}
